/* ═══ AgDrawer — Anagogia Design System ═══ */

import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCalqueEchap } from '../utils/useCalqueEchap';
import styles from './AgDrawer.module.css';

interface AgDrawerProps {
  /** Whether the drawer is open (temporary/persistent modes) */
  open?: boolean;
  /** Called when the drawer requests to close (overlay click, Escape key) */
  onClose?: () => void;
  /** Display mode: permanent (always visible), temporary (overlay), persistent (no overlay) */
  variant?: 'permanent' | 'temporary' | 'persistent';
  /** Side the drawer appears from */
  anchor?: 'left' | 'right';
  /** Drawer width */
  width?: number | string;
  /** Custom z-index override (set via CSS variable) */
  zIndex?: number;
  /** Content */
  children: React.ReactNode;
  /** Additional class name for the drawer panel */
  className?: string;
  /** Inline styles for the drawer panel */
  style?: React.CSSProperties;
}

/**
 * Panneau latéral coulissant (navigation, détails).
 * @category Overlays
 */
function AgDrawer({
  open = true,
  onClose,
  variant = 'permanent',
  anchor = 'left',
  width = 256,
  zIndex,
  children,
  className,
  style,
}: AgDrawerProps) {
  // Échap (hors `permanent`) : le panneau ouvert s'inscrit dans la pile des
  // calques (voir `utils/calqueStack.ts`) et ne se ferme que s'il en occupe le
  // sommet — une fenêtre ouverte après lui passe donc avant. En `persistent`
  // (panneau de l'assistant, peint au-dessus des fenêtres), il remonte au
  // sommet dès que le focus entre dans le panneau : Échap ferme alors le
  // panneau où l'on tape, pas la fenêtre de dessous.
  const panneauRef = useRef<HTMLElement>(null);
  useCalqueEchap(variant !== 'permanent' && open && Boolean(onClose), () => onClose?.(), {
    actif: Boolean(onClose),
    persistant: variant === 'persistent',
    conteneur: panneauRef,
  });

  const cssVars = {
    ...(zIndex != null ? { '--ag-drawer-z': String(zIndex) } : {}),
  } as React.CSSProperties;

  const drawerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    ...cssVars,
    ...style,
  };

  // --- Permanent variant ---
  if (variant === 'permanent') {
    const cls = [styles.drawer, styles.permanent, className]
      .filter(Boolean)
      .join(' ');

    return (
      <aside className={cls} style={drawerStyle}>
        {children}
      </aside>
    );
  }

  // --- Temporary variant (with overlay) ---
  if (variant === 'temporary') {
    const panelCls = [
      styles.drawer,
      styles.temporary,
      styles[anchor],
      !open && styles.temporaryHidden,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const content = (
      <>
        {open && (
          <div
            className={styles.overlay}
            style={cssVars}
            onClick={onClose}
            aria-hidden="true"
          />
        )}
        <aside ref={panneauRef} className={panelCls} style={drawerStyle} role="dialog" aria-modal="true">
          {children}
        </aside>
      </>
    );

    return createPortal(content, document.body);
  }

  // --- Persistent variant (no overlay) ---
  const panelCls = [
    styles.drawer,
    styles.persistent,
    styles[anchor],
    !open && styles.persistentHidden,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <aside ref={panneauRef} className={panelCls} style={drawerStyle}>
      {children}
    </aside>
  );

  return createPortal(content, document.body);
}

export { AgDrawer };
export type { AgDrawerProps };
export default AgDrawer;
